/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useGetCustomerBoSubscription } from '@utils/api/back-office/customer-bo/use-get-subscription-bo';
import { useGetCustomerBoUsage } from '@utils/api/back-office/customer-bo/use-get-usage-bo';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import cn from 'classnames';
import { formatCurrency } from '@utils/helpers/currency';
import { formatCustomModelYearlyUsage } from '@utils/helpers/custom-model-usage';
import { SupportedCurrency } from '@utils/types/organization/organization';
import {
  Calendar,
  CheckCircle2,
  CreditCard,
  Gauge,
  Pause,
  Play,
  XCircle
} from 'lucide-react';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Params, useParams } from 'react-router-dom';

type SelectedSubscription = {
  autoRenew: boolean;
  billing_channel?: string;
  cancel_date: string;
  end_date: string;
  id: number;
  plan_id: number;
  sku: string;
  start_date: string;
  suspended: boolean;
};

type Props = {
  subscriptionId: number;
  selectedSubscription: SelectedSubscription;
  isEditModalOpen: true;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  isPast: boolean;
  handleDelete(): void;
  handleSuspend(suspended: boolean): void;
  isDeletingSubscription: boolean;
  currency?: SupportedCurrency;
};

type UsageRow = {
  period_start: string;
  period_end: string;
  stt_units_used: number;
  alignment_units_used: number;
  translation_characters_used: number;
  custom_models_trained: number;
  custom_models_hosted: number;
  cost_billed: number;
  billed: boolean;
};

export const CustomerBoSubscriptionDetails = ({
  subscriptionId,
  selectedSubscription,
  isEditModalOpen,
  setIsEditModalOpen,
  isPast,
  handleDelete,
  handleSuspend,
  isDeletingSubscription,
  currency = 'EUR'
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();

  const { data: usageDetails, isLoading: usageIsLoading } =
    useGetCustomerBoUsage(Number(customerId), selectedSubscription?.id ?? 0);

  const { data: subscriptionDetails } = useGetCustomerBoSubscription(
    Number(customerId),
    subscriptionId
  );

  const columns = useMemo<DataGridColumn<UsageRow>[]>(
    () => [
      {
        header: t('period-start'),
        field: 'period_start',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'date',
        filterKey: 'period_start',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('period-end'),
        field: 'period_end',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        filterKey: 'period_end',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('stt-units'),
        field: 'stt_units_used',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString()
      },
      {
        header: t('alignment-units'),
        field: 'alignment_units_used',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString()
      },
      {
        header: t('translation-characters'),
        field: 'translation_characters_used',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString()
      },
      {
        header: t('custom-models-trained'),
        field: 'custom_models_trained',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString()
      },
      {
        header: t('custom-models-hosted'),
        field: 'custom_models_hosted',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString()
      },
      {
        header: t('cost-billed'),
        field: 'cost_billed',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => {
          const value = Number(getValue() ?? 0);
          return formatCurrency(value, currency);
        }
      },
      {
        header: t('billed'),
        field: 'billed',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const isBilled = getValue();
          return (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: isBilled
                  ? 'rgba(34, 197, 94, 0.1)'
                  : 'rgba(234, 179, 8, 0.1)',
                color: isBilled ? 'rgb(22, 163, 74)' : 'rgb(202, 138, 4)'
              }}
            >
              {isBilled ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              {isBilled ? t('yes') : t('no')}
            </span>
          );
        }
      }
    ],
    [t]
  );

  const rows: UsageRow[] =
    usageDetails?.result.map(u => ({
      period_start: u.period_start,
      period_end: u.period_end,
      stt_units_used: u.stt_units_used,
      alignment_units_used: u.alignment_units_used,
      translation_characters_used: u.translation_characters_used,
      custom_models_trained: u.custom_models_trained,
      custom_models_hosted: u.custom_models_hosted,
      cost_billed: u.cost_billed,
      billed: u.billed
    })) ?? [];

  const StatusBadge = ({
    active,
    activeLabel,
    inactiveLabel
  }: {
    active: boolean;
    activeLabel: string;
    inactiveLabel: string;
  }) => (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
      style={{
        backgroundColor: active
          ? 'rgba(34, 197, 94, 0.1)'
          : 'rgba(239, 68, 68, 0.1)',
        color: active ? 'rgb(22, 163, 74)' : 'rgb(220, 38, 38)'
      }}
    >
      {active ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      {active ? activeLabel : inactiveLabel}
    </span>
  );

  const InfoCard = ({
    icon: Icon,
    title,
    children,
    accentColor
  }: {
    icon: React.ElementType;
    title: string;
    children: React.ReactNode;
    accentColor: string;
  }) => (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: getColour('thirdLayer'),
        border: `1px solid ${getColour('hairline')}`
      }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{
          borderBottom: `1px solid ${getColour('hairline')}`,
          background: `linear-gradient(135deg, ${accentColor}08 0%, transparent 100%)`
        }}
      >
        <div
          className="flex h-8 w-8 items-center justify-center rounded-2xl"
          style={{
            backgroundColor: `${accentColor}15`,
            color: accentColor
          }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <span
          className="text-sm font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {title}
        </span>
      </div>
      <div className="px-4 py-3 space-y-2">{children}</div>
    </div>
  );

  const InfoRow = ({
    label,
    value,
    highlight
  }: {
    label: string;
    value: React.ReactNode;
    highlight?: boolean;
  }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs" style={{ color: getColour('inactiveText') }}>
        {label}
      </span>
      <span
        className={cn('text-xs', highlight && 'font-medium')}
        style={{ color: getColour('normalText') }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <Modal
      isOpen={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      size="x-large"
      title={
        <div className="flex items-center gap-3">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-2xl"
            style={{
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              color: 'rgb(99, 102, 241)'
            }}
          >
            <CreditCard className="h-4 w-4" />
          </div>
          <div>
            <span className="text-base font-semibold">
              {t('subscription')} #{selectedSubscription.id}
            </span>
            <span
              className="ml-3 text-xs font-normal"
              style={{ color: getColour('inactiveText') }}
            >
              {selectedSubscription.sku}
            </span>
          </div>
        </div>
      }
      caption={t('subscription-and-usage-details')}
      footerActions={[
        <Button
          key="suspend"
          label={selectedSubscription.suspended ? t('unsuspend') : t('suspend')}
          onClick={() => handleSuspend(!selectedSubscription.suspended)}
          variant={selectedSubscription.suspended ? 'success' : 'danger'}
          size="medium"
          disabled={isPast}
          icon={
            selectedSubscription.suspended ? (
              <Play className="-ml-0.5 h-4 w-4" aria-hidden="true" />
            ) : (
              <Pause className="-ml-0.5 h-4 w-4" aria-hidden="true" />
            )
          }
        />,
        <Button
          key="cancel"
          variant="primary"
          onClick={handleDelete}
          label={
            isDeletingSubscription ? t('canceling') : t('cancel-subscription')
          }
          disabled={isPast || isDeletingSubscription}
          size="medium"
        />
      ]}
    >
      {/* Info Cards Section */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 mb-6">
        {/* Dates Card */}
        <InfoCard
          icon={Calendar}
          title={t('dates')}
          accentColor="rgb(99, 102, 241)"
        >
          <InfoRow
            label={t('start-date')}
            value={subscriptionDetails?.result?.start_date || '—'}
          />
          <InfoRow
            label={t('end-date')}
            value={subscriptionDetails?.result?.end_date || '—'}
          />
          <InfoRow
            label={t('cancel-date')}
            value={subscriptionDetails?.result?.cancel_date || '—'}
          />
        </InfoCard>

        {/* Subscription Status Card */}
        <InfoCard
          icon={CreditCard}
          title={t('about-subscription')}
          accentColor="rgb(139, 92, 246)"
        >
          <InfoRow
            label={t('auto-renew')}
            value={
              <StatusBadge
                active={subscriptionDetails?.result?.auto_renew ?? false}
                activeLabel={t('yes')}
                inactiveLabel={t('no')}
              />
            }
          />
          <InfoRow
            label={t('suspended')}
            value={
              <StatusBadge
                active={!subscriptionDetails?.result?.suspended}
                activeLabel={t('no')}
                inactiveLabel={t('yes')}
              />
            }
          />
          <InfoRow
            label={t('billing-channel')}
            value={
              <span
                className="inline-flex items-center rounded-2xl px-2 py-0.5 text-xs font-medium"
                style={{
                  backgroundColor: getColour('appBackground'),
                  color: getColour('normalText')
                }}
              >
                {subscriptionDetails?.result?.billing_channel?.toUpperCase?.() ||
                  '—'}
              </span>
            }
          />
        </InfoCard>

        {/* Availability Card */}
        <InfoCard
          icon={Gauge}
          title={t('availability')}
          accentColor="rgb(16, 185, 129)"
        >
          <InfoRow
            label={t('stt-units')}
            value={
              subscriptionDetails?.result?.available_stt_units?.toLocaleString() ??
              '—'
            }
            highlight
          />
          <InfoRow
            label={t('alignment-units')}
            value={
              subscriptionDetails?.result?.available_alignment_units?.toLocaleString() ??
              '—'
            }
            highlight
          />
          <InfoRow
            label={t('translation-characters')}
            value={
              subscriptionDetails?.result?.available_translation_characters !=
              null
                ? Number(
                    subscriptionDetails.result.available_translation_characters
                  ).toLocaleString()
                : '—'
            }
            highlight
          />
          <InfoRow
            label={t('automatic-training')}
            value={
              subscriptionDetails?.result?.available_custom_models_trained?.toLocaleString() ??
              '—'
            }
            highlight
          />
          <InfoRow
            label={t('custom-models-this-year')}
            value={
              subscriptionDetails?.result
                ? formatCustomModelYearlyUsage(
                    subscriptionDetails.result.custom_model_yearly_used,
                    subscriptionDetails.result.custom_model_yearly_limit,
                    t('unlimited')
                  )
                : '—'
            }
            highlight
          />
        </InfoCard>
      </div>

      {/* Usage History Section */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          backgroundColor: getColour('thirdLayer'),
          border: `1px solid ${getColour('hairline')}`
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: `1px solid ${getColour('hairline')}` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-2xl"
              style={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                color: 'rgb(245, 158, 11)'
              }}
            >
              <Gauge className="h-4 w-4" />
            </div>
            <span
              className="text-sm font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('usage-history')}
            </span>
          </div>
          <span
            className="text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {rows.length} {t('records')}
          </span>
        </div>

        <div className="p-4">
          {usageIsLoading ? (
            <div
              className="flex items-center justify-center py-12"
              style={{ color: getColour('inactiveText') }}
            >
              <span className="text-sm">{t('loading')}...</span>
            </div>
          ) : (
            <DataGridV2<UsageRow>
              data={rows}
              columns={columns}
              enableRowSelection={false}
              enablePagination
              manualPagination
              enableSorting
              manualSorting
              enableFiltering
              manualFiltering
              totalItems={usageDetails?.total_results ?? rows.length}
              pageSize={25}
              pageSizeOptions={[25, 50, 75, 100]}
              showColumnFilters={false}
              filterMode="popover"
              height="45vh"
              dense
              grid={false}
              striped
              loading={usageIsLoading || !usageDetails}
              getRowId={row => `${row.period_start}-${row.period_end}`}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
